/**
 * Retour haptique abstrait. Par défaut au-dessus de `navigator.vibrate` ; un backend
 * enfichable (`setHapticBackend`) permettra de brancher **Capacitor Haptics** en Phase 5
 * sans toucher aux appelants. Coupé par le réglage Haptique (F-SETTINGS-002) ; ignoré en
 * silence si le matériel ne vibre pas. Motifs : docs/05 §8.
 */
import { getSettings } from './settings';

export type VibratePattern = number | number[];
export type HapticEvent = 'tap' | 'refuse' | 'lifeLost' | 'win' | 'round' | 'room';

/** Motifs de vibration par événement, en ms. `tap` valide = pas d'haptique (docs/05 §8). */
export const HAPTIC_PATTERNS: Record<HapticEvent, VibratePattern | null> = {
  tap: null,
  refuse: 10, // tick léger
  lifeLost: [30, 30, 30], // double impulsion
  win: [40, 60, 40],
  round: 40,
  room: 20,
};

/** Cible pluggable : Web (`navigator.vibrate`) aujourd'hui, natif (Capacitor) en Phase 5. */
export interface HapticBackend {
  vibrate(pattern: VibratePattern): void;
}

const webBackend: HapticBackend = {
  vibrate(pattern) {
    try {
      navigator.vibrate?.(pattern);
    } catch {
      /* pas de vibreur : on ignore */
    }
  },
};

let backend: HapticBackend = webBackend;

/** Branche un backend natif (Capacitor Haptics). Appelé en Phase 5. */
export function setHapticBackend(b: HapticBackend): void {
  backend = b;
}

/** Déclenche l'haptique d'un événement, si activée et si un motif existe. */
export function haptic(event: HapticEvent): void {
  if (!getSettings().haptics) return;
  const pattern = HAPTIC_PATTERNS[event];
  if (pattern == null) return;
  backend.vibrate(pattern);
}
